import React from 'react';
import { motion } from 'framer-motion';
import { CogIcon, CalendarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';

const FEATURE_TITLE = "Monitoring Plans";
const FEATURE_DESCRIPTION = "Customized monitoring recommendations with timeframe-specific guidelines, allergy tracking, and follow-up protocols.";

const MonitoringPlansPage: React.FC = () => {
  return (
    <div className="py-16 bg-background">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-text tracking-tight sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {FEATURE_TITLE}
          </motion.h1>
          <motion.p 
            className="mt-4 text-xl text-text max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {FEATURE_DESCRIPTION}
          </motion.p>
        </div>

        <div className="mt-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <CogIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Customized Protocols</h3>
              <p className="text-text">
                We generate tailored monitoring recommendations that are specific to the 
                patient's condition, treatment plan, and risk factors.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Patient-specific monitoring parameters</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Risk-adjusted follow-up schedules</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Condition-specific laboratory recommendations</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <CalendarIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Timeframe Guidelines</h3>
              <p className="text-text">
                Our monitoring plans include clear timeframe-specific guidelines for short-term, 
                mid-term, and long-term follow-up to ensure comprehensive care.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Immediate post-treatment monitoring parameters</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Mid-course evaluation criteria</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Long-term follow-up recommendations</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Safety Monitoring</h3>
              <p className="text-text">
                Our system includes comprehensive allergy tracking and adverse reaction 
                monitoring to ensure patient safety throughout treatment.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Allergy cross-reference with medications</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Adverse reaction tracking and alerting</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Drug interaction monitoring</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="mt-16 bg-section-light p-8 rounded-lg shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-text mb-6">Sample Monitoring Plan</h2>
          
          <div className="border border-border rounded-lg overflow-hidden bg-white">
            <div className="bg-primary px-4 py-3">
              <h3 className="text-lg font-semibold text-text-light">Complicated UTI with Extended-Spectrum Beta-Lactamase (ESBL) E. coli</h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="h-8 w-8 rounded-full bg-primary text-text-light flex items-center justify-center mr-3">
                      <span className="font-bold">1</span>
                    </div>
                    <h4 className="font-semibold text-lg">Immediate (0-7 days)</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Daily assessment of fever and symptoms</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Fluid intake/output monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Renal function tests at day 3</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Check for drug interactions daily</span>
                    </li>
                  </ul>
                </div>
                
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="h-8 w-8 rounded-full bg-primary text-text-light flex items-center justify-center mr-3">
                      <span className="font-bold">2</span>
                    </div>
                    <h4 className="font-semibold text-lg">Short-term (8-14 days)</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Repeat urine culture at day 10</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Complete blood count with differential</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Renal and liver function assessment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Clinical assessment for resolution</span>
                    </li>
                  </ul>
                </div>
                
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="h-8 w-8 rounded-full bg-primary text-text-light flex items-center justify-center mr-3">
                      <span className="font-bold">3</span>
                    </div>
                    <h4 className="font-semibold text-lg">Long-term (1-3 months)</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Monthly urine analysis for 3 months</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Renal function monitoring at 1 and 3 months</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Ultrasound of urinary tract at 3 months</span>
                    </li>
                    <li className="flex items-start">
                      <span className="h-5 w-5 text-primary flex-shrink-0">•</span>
                      <span className="ml-2">Assessment for recurrence and complications</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-warning-bg border border-warning-border rounded-lg">
                <h4 className="font-semibold text-warning-text mb-2">Allergy and Safety Alerts</h4>
                <ul className="space-y-2 text-sm text-warning-text">
                  <li className="flex items-start">
                    <span className="h-5 w-5 flex-shrink-0">⚠️</span>
                    <span className="ml-2">Monitor for development of C. difficile infection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="h-5 w-5 flex-shrink-0">⚠️</span>
                    <span className="ml-2">Assess for signs of drug-induced nephrotoxicity</span>
                  </li>
                  <li className="flex items-start">
                    <span className="h-5 w-5 flex-shrink-0">⚠️</span>
                    <span className="ml-2">Patient has previous allergic reaction to penicillins - avoid related antibiotics</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Schedule a Demo
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MonitoringPlansPage; 